import { type ReactNode, useEffect, useRef } from "react";
import { useFormHeader } from "../FormHeaderContext";
import styles from "./EventFormShell.module.css";

type FormMode = "create" | "edit";

interface EventFormShellProps {
	mode: FormMode;
	isValid: boolean;
	isPending: boolean;
	onSave: () => void;
	/** Plan 09 fills this in for edit-mode whole-series banner (D-19). */
	recurringBanner?: ReactNode;
	children: ReactNode;
}

/**
 * Phase 37 — Shared form shell wrapping the 4 type-specific forms.
 *
 * Layout: (optional recurringBanner) + body. Save/Cancel buttons live in the
 * AddEventSheet header — this shell registers its save state into FormHeaderContext
 * so the Save button on the right side of the sheet header reflects current validation.
 *
 * Uses a ref for `onSave` so the registration effect only re-runs on
 * isValid / isPending / mode changes — not on every parent render where the
 * handler closure is rebuilt. Without this, the form would update its parent's
 * context state on every render and cause Maximum-update-depth errors.
 */
export function EventFormShell({
	mode,
	isValid,
	isPending,
	onSave,
	recurringBanner,
	children,
}: EventFormShellProps) {
	const { setFormState } = useFormHeader();
	const onSaveRef = useRef(onSave);
	onSaveRef.current = onSave;

	useEffect(() => {
		setFormState({
			isValid,
			isPending,
			mode,
			onSave: () => onSaveRef.current(),
		});
		return () => setFormState(null);
	}, [setFormState, isValid, isPending, mode]);

	return (
		<div className={styles.shell}>
			{recurringBanner ? (
				<div className={styles.bannerSlot}>{recurringBanner}</div>
			) : null}
			<div className={styles.body}>{children}</div>
		</div>
	);
}
